import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { summarizeEinvoiceXml, type EinvoiceXmlParty, type EinvoiceXmlSummary } from '../utils/einvoiceXmlSummary';
import { formatGermanNumber } from '../../../utils/parseGermanNumber';

export function isXmlInvoiceFile(row: { mime_type?: string | null; filename?: string | null }): boolean {
  const mime = String(row.mime_type ?? '').toLowerCase();
  return mime.includes('xml') || String(row.filename ?? '').toLowerCase().endsWith('.xml');
}

function money(value: number | null, currency: string | null): string {
  if (value == null) return '—';
  return `${formatGermanNumber(value)} ${currency && currency !== 'EUR' ? currency : '€'}`;
}

function date(value: string | null): string {
  if (!value) return '—';
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
}

function Party({ title, party }: { title: string; party: EinvoiceXmlParty }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-app-text-muted">{title}</p>
      <p className="text-sm font-medium text-app-text">{party.name ?? '—'}</p>
      {party.street ? <p className="text-sm text-app-text-muted">{party.street}</p> : null}
      {party.postcodeCity ? <p className="text-sm text-app-text-muted">{party.postcodeCity}{party.country && party.country !== 'DE' ? `, ${party.country}` : ''}</p> : null}
      {party.vatId ? <p className="text-xs text-app-text-subtle">USt-IdNr. {party.vatId}</p> : null}
    </div>
  );
}

function SummaryView({ summary }: { summary: EinvoiceXmlSummary }) {
  const isCredit = summary.typeCode === '381';
  return (
    <div className="space-y-5 p-5 text-app-text">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold">
          {isCredit ? 'Gutschrift' : 'Rechnung'} {summary.invoiceNumber ?? ''}
        </h3>
        <span className="rounded-full bg-app-surface-muted px-2 py-0.5 text-xs text-app-text-muted">
          E-Rechnung ({summary.syntax === 'CII' ? 'ZUGFeRD/XRechnung CII' : 'XRechnung UBL'})
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Party title="Rechnungssteller" party={summary.seller} />
        <Party title="Rechnungsempfänger" party={summary.buyer} />
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <dt className="text-app-text-muted">Rechnungsdatum</dt>
        <dd>{date(summary.issueDate)}</dd>
        <dt className="text-app-text-muted">Fällig am</dt>
        <dd>{date(summary.dueDate)}</dd>
        {summary.buyerReference ? (
          <>
            <dt className="text-app-text-muted">Leitweg-ID / Referenz</dt>
            <dd className="break-all">{summary.buyerReference}</dd>
          </>
        ) : null}
        {summary.iban ? (
          <>
            <dt className="text-app-text-muted">IBAN</dt>
            <dd className="font-mono text-xs">{summary.iban}</dd>
          </>
        ) : null}
      </dl>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead>
            <tr className="border-b border-app-border text-left text-xs text-app-text-muted">
              <th className="py-1.5 pr-2 font-medium">Pos.</th>
              <th className="py-1.5 pr-2 font-medium">Bezeichnung</th>
              <th className="py-1.5 pr-2 text-right font-medium">Menge</th>
              <th className="py-1.5 pr-2 text-right font-medium">Einzelpreis</th>
              <th className="py-1.5 pr-2 text-right font-medium">USt.</th>
              <th className="py-1.5 text-right font-medium">Gesamt</th>
            </tr>
          </thead>
          <tbody>
            {summary.lines.map((line, idx) => (
              <tr key={`${line.id ?? idx}`} className="border-b border-app-border/60 align-top">
                <td className="py-1.5 pr-2 tabular-nums text-app-text-muted">{line.id ?? idx + 1}</td>
                <td className="py-1.5 pr-2">{line.name ?? '—'}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">
                  {line.quantity != null ? formatGermanNumber(line.quantity, { minimumFractionDigits: 0 }) : '—'} {line.unit ?? ''}
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{money(line.unitPrice, summary.currency)}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{line.taxRate != null ? `${line.taxRate} %` : '—'}</td>
                <td className="py-1.5 text-right tabular-nums">{money(line.lineTotal, summary.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-app-text-muted">Netto</span><span className="tabular-nums">{money(summary.totals.net, summary.currency)}</span></div>
        {summary.taxes.map((tax, idx) => (
          <div key={idx} className="flex justify-between">
            <span className="text-app-text-muted">USt. {tax.rate != null ? `${tax.rate} %` : ''} {tax.category && tax.category !== 'S' ? `(${tax.category})` : ''}</span>
            <span className="tabular-nums">{money(tax.tax, summary.currency)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t border-app-border pt-1 font-semibold"><span>Brutto</span><span className="tabular-nums">{money(summary.totals.gross, summary.currency)}</span></div>
        {summary.totals.due != null && summary.totals.due !== summary.totals.gross ? (
          <div className="flex justify-between"><span className="text-app-text-muted">Zahlbetrag</span><span className="tabular-nums">{money(summary.totals.due, summary.currency)}</span></div>
        ) : null}
      </div>
      {summary.paymentTerms ? <p className="text-sm text-app-text-muted">{summary.paymentTerms}</p> : null}
      {summary.notes.length > 0 ? (
        <div className="space-y-1 text-xs text-app-text-subtle">
          {summary.notes.map((note, idx) => <p key={idx}>{note}</p>)}
        </div>
      ) : null}
    </div>
  );
}

/** Lesbare Darstellung einer reinen XML-E-Rechnung statt eines PDF-Viewers. */
export function EinvoiceXmlView({ url, className = '' }: { url: string; className?: string }) {
  const [state, setState] = React.useState<{ loading: boolean; xml: string | null; error: string | null }>({
    loading: true,
    xml: null,
    error: null,
  });

  React.useEffect(() => {
    let cancelled = false;
    setState({ loading: true, xml: null, error: null });
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error || 'E-Rechnung konnte nicht geladen werden.');
        }
        return res.text();
      })
      .then((xml) => {
        if (!cancelled) setState({ loading: false, xml, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, xml: null, error: err instanceof Error ? err.message : 'Fehler beim Laden.' });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const summary = React.useMemo(() => (state.xml ? summarizeEinvoiceXml(state.xml) : null), [state.xml]);

  return (
    <div className={`app-scrollbar min-h-0 flex-1 overflow-y-auto bg-app-surface ${className}`}>
      {state.loading ? <div className="p-6"><InlineLoadingState label="E-Rechnung wird geladen …" /></div> : null}
      {state.error ? <p className="p-6 text-sm text-app-text-muted">{state.error}</p> : null}
      {summary ? <SummaryView summary={summary} /> : null}
      {!state.loading && state.xml && !summary ? (
        <pre className="whitespace-pre-wrap break-all p-4 text-xs text-app-text-muted">{state.xml}</pre>
      ) : null}
    </div>
  );
}
